if (!customElements.get('review-widget-slider')) {
  class ReviewWidgetSlider extends HTMLElement {
    constructor() {
      super();
      this.defaultMobileSlideIndex = 2;
      this.layoutMode = null;
      this.onArrowClick = this.onArrowClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-viewport]');
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.prevButton = this.querySelector('[data-action="prev"]');
      this.nextButton = this.querySelector('[data-action="next"]');

      if (!this.viewport || !this.track || !this.slides.length) return;
      this.layoutMode = this.getLayoutMode();

      if (this.prevButton) this.prevButton.addEventListener('click', this.onArrowClick);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onArrowClick);

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.viewport);
      this.slides.forEach((slide) => this.resizeObserver.observe(slide));

      requestAnimationFrame(() => {
        this.measure();
        this.resetToDefaultSlide(this.layoutMode);
        this.updateState();
      });
    }

    disconnectedCallback() {
      if (this.prevButton) this.prevButton.removeEventListener('click', this.onArrowClick);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onArrowClick);
      if (this.viewport) this.viewport.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    onResize() {
      this.measure();
      const nextLayoutMode = this.getLayoutMode();

      if (nextLayoutMode !== this.layoutMode) {
        this.layoutMode = nextLayoutMode;
        this.resetToDefaultSlide(nextLayoutMode);
      }

      this.updateState();
    }

    onScroll() {
      this.updateState();
    }

    onArrowClick(event) {
      if (!this.viewport) return;

      const direction = event.currentTarget.dataset.action === 'next' ? 1 : -1;
      const nextPosition = this.viewport.scrollLeft + (this.slideStep * direction);

      this.viewport.scrollTo({
        left: this.clamp(nextPosition, 0, this.maxScroll),
        behavior: 'smooth'
      });
    }

    measure() {
      if (!this.viewport || !this.track || !this.slides.length) return;

      this.slideStep = this.getSlideStep();
      this.maxScroll = Math.max(0, this.track.scrollWidth - this.viewport.clientWidth);
      this.classList.toggle('is-static', this.maxScroll <= 1);
    }

    getSlideStep() {
      if (this.slides.length < 2) return this.viewport.clientWidth;
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }

    resetToDefaultSlide(layoutMode) {
      if (!this.viewport || !this.slides.length) return;

      if (this.maxScroll <= 1) {
        this.viewport.scrollLeft = 0;
        return;
      }

      if (layoutMode === 'mobile') {
        const targetIndex = Math.min(this.defaultMobileSlideIndex, this.slides.length - 1);
        const targetSlide = this.slides[targetIndex];

        if (!targetSlide) return;

        this.viewport.scrollLeft = this.clamp(this.getCenteredScrollLeft(targetSlide), 0, this.maxScroll);
        return;
      }

      this.viewport.scrollLeft = 0;
    }

    getCenteredScrollLeft(slide) {
      const viewportRect = this.viewport.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();

      return this.viewport.scrollLeft + (slideRect.left - viewportRect.left) - ((viewportRect.width - slideRect.width) / 2);
    }

    updateState() {
      if (!this.prevButton || !this.nextButton) return;

      const tolerance = 2;
      const isStatic = this.maxScroll <= 1;

      this.prevButton.toggleAttribute('disabled', isStatic || this.viewport.scrollLeft <= tolerance);
      this.nextButton.toggleAttribute('disabled', isStatic || this.viewport.scrollLeft >= this.maxScroll - tolerance);
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }

    getLayoutMode() {
      return window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
    }
  }

  customElements.define('review-widget-slider', ReviewWidgetSlider);
}
